import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { X, Server as ServerIcon, AlertTriangle, Info } from 'lucide-react';
import { servers as serverApi, customers as customerApi } from '../api';

/**
 * Eine dedizierte Hetzner-Maschine in den PC übernehmen.
 *
 * ÜBERNEHMEN, NICHT ANLEGEN. Die Maschine existiert bereits — hier entsteht nur
 * der Datensatz dazu. Bestellt wird bei dedizierter Hardware von Hand in Robot:
 * Einrichtungsgebühr und Mindestlaufzeit sind nichts, was hinter einem Knopf
 * ausgelöst werden sollte, den man versehentlich trifft.
 *
 * Der Zugang hängt am **Kunden**, nicht an der Maschine — deshalb steht die
 * Kundenauswahl vor der Liste und nicht daneben. Ohne hinterlegten Robot-Zugang
 * gibt es nichts zu sehen, und die Antwort sagt das dann auch so.
 */
export default function RobotImportModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [customerId, setCustomerId] = useState('');
  const [ownership, setOwnership] = useState<'CUSTOMER_PROVIDED' | 'DEDICATED_JUNI'>(
    'CUSTOMER_PROVIDED',
  );
  const [meldung, setMeldung] = useState<string[]>([]);
  const [fehler, setFehler] = useState('');

  const { data: kunden } = useQuery({
    queryKey: ['customers'],
    queryFn: () => customerApi.list(),
  });

  const {
    data: robot,
    isFetching,
    error: robotFehler,
  } = useQuery({
    queryKey: ['servers', 'robot', customerId],
    queryFn: () => serverApi.robotList(customerId),
    enabled: Boolean(customerId),
    retry: false, // Robot sperrt nach drei Fehlversuchen die IP. Nie wiederholen.
  });

  const uebernehmen = useMutation({
    mutationFn: (serverNumber: number) =>
      serverApi.robotImport({ customerId, serverNumber, ownership }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['servers'] });
      setFehler('');
      setMeldung([r.message, ...(r.hinweise ?? [])]);
    },
    onError: (err: unknown) => {
      setMeldung([]);
      setFehler(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          'Übernehmen fehlgeschlagen.',
      );
    },
  });

  const robotMeldung =
    (robotFehler as { response?: { data?: { message?: string } } })?.response?.data?.message ??
    (robotFehler ? 'Robot-Abfrage gescheitert.' : '');

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4">
      <div className="mt-8 flex max-h-[88vh] w-full max-w-3xl flex-col overflow-y-auto rounded-xl bg-surface shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <div>
            <h2 className="text-base font-semibold text-slate-900">{t('robot.title')}</h2>
            <p className="text-xs text-slate-500">{t('robot.explain')}</p>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          >
            <X size={18} />
          </button>
        </div>

        <div className="space-y-3 px-6 py-4 text-xs">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-slate-500">{t('robot.pickCustomer')}</label>
              <select
                value={customerId}
                onChange={(e) => {
                  setCustomerId(e.target.value);
                  setMeldung([]);
                  setFehler('');
                }}
                className="w-full rounded border border-slate-300 px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-brand-500"
              >
                <option value="">—</option>
                {(kunden ?? []).map((k) => (
                  <option key={k.id} value={k.id}>
                    {k.companyName}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-slate-500">{t('robot.ownership')}</label>
              <select
                value={ownership}
                onChange={(e) => setOwnership(e.target.value as typeof ownership)}
                className="w-full rounded border border-slate-300 px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-brand-500"
              >
                <option value="CUSTOMER_PROVIDED">{t('robot.ownershipCustomer')}</option>
                <option value="DEDICATED_JUNI">{t('robot.ownershipJuni')}</option>
              </select>
            </div>
          </div>

          {fehler && <div className="text-red-600">{fehler}</div>}
          {meldung.length > 0 && (
            <div className="space-y-1 rounded border border-green-200 bg-green-50 px-2.5 py-2 text-green-800">
              {meldung.map((z, i) => (
                <p key={i}>{z}</p>
              ))}
            </div>
          )}

          {/* Ein abgelehnter Zugang ist hier die häufigste Antwort. Er kommt als
              lesbarer Satz vom Server — samt der Warnung, nicht gleich noch
              einmal zu probieren. */}
          {robotMeldung && (
            <div className="flex gap-2 rounded border border-amber-200 bg-amber-50 px-2.5 py-2 text-amber-800">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" />
              <p>{robotMeldung}</p>
            </div>
          )}

          {!customerId ? null : isFetching ? (
            <div className="h-8 animate-pulse rounded bg-slate-100" />
          ) : !robot?.server?.length ? (
            !robotMeldung && <p className="italic text-slate-400">{t('robot.none')}</p>
          ) : (
            <ul className="space-y-1.5">
              {robot.server.map((m) => (
                <li
                  key={m.serverNumber}
                  className="flex items-center justify-between gap-2 rounded border border-slate-200 bg-surface px-2.5 py-2"
                >
                  <div className="min-w-0">
                    <p className="flex items-center gap-1.5 text-slate-700">
                      <ServerIcon size={12} className="text-slate-400" />
                      <span className="font-medium">{m.name || `#${m.serverNumber}`}</span>
                      <span className="font-mono text-slate-400">{m.ipAddress}</span>
                    </p>
                    <p className="text-slate-400">
                      {m.product} · {m.datacenter} · {m.traffic}
                      {m.cancelled && (
                        <span className="ml-1 text-amber-700">
                          · {t('robot.cancelled', { until: m.paidUntil })}
                        </span>
                      )}
                    </p>
                  </div>
                  {m.bereitsImPc ? (
                    <span className="shrink-0 text-slate-400">{t('robot.alreadyHere')}</span>
                  ) : (
                    <button
                      onClick={() => uebernehmen.mutate(m.serverNumber)}
                      disabled={uebernehmen.isPending}
                      className="shrink-0 rounded bg-brand-600 px-3 py-1.5 font-medium text-white hover:bg-brand-700 disabled:opacity-60"
                    >
                      {uebernehmen.isPending ? t('robot.taking') : t('robot.take')}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}

          <p className="flex gap-2 border-t border-slate-100 pt-3 text-slate-400">
            <Info size={13} className="mt-0.5 shrink-0" />
            Bestellt wird bei dedizierten Maschinen von Hand in Robot — hier werden nur
            vorhandene übernommen.
          </p>
        </div>
      </div>
    </div>
  );
}
